import React, { useState, Component } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import api from './api/client';
import toast from 'react-hot-toast';
import Layout from './components/layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import AdminPanel from './pages/AdminPanel';
import Attendance from './pages/Attendance';
import PurchaseOrders from './pages/PurchaseOrders';
import Leave from './pages/Leave';
import Profile from './pages/Profile';
import GroupChat from './pages/GroupChat';
import Factory from './pages/Factory';
import ExpenseRequests from './pages/ExpenseRequests';
import WorkOrders from './pages/WorkOrders';
import FactoryWorkOrders from './pages/FactoryWorkOrders';
import WOTracker from './pages/WOTracker'; // EXP31b
import TransportDocs from './pages/TransportDocs'; // EXP21
import Vendors from './pages/Vendors';
import Accounts, { SupervisorLedger } from './pages/Accounts';
import AvProducts from './pages/AvProducts'; // AVB3-import
import HRDashboard from './pages/HRDashboard';
import QuotationRequests from './pages/QuotationRequests';

function ForcePasswordChange() {
  const { loadUser } = useAuth();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.newPassword !== form.confirm) { toast.error('Passwords do not match'); return; }
    if (form.newPassword.length < 8) { toast.error('Min 8 characters'); return; }
    setSaving(true);
    try { await api.put('/auth/change-password', { currentPassword: form.currentPassword, newPassword: form.newPassword }); toast.success('Password changed!'); await loadUser(); } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    setSaving(false);
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-800 to-brand-900 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Change Your Password</h2>
        <p className="text-sm text-gray-500 mb-6">You must change your temporary password before continuing.</p>
        <form onSubmit={submit} className="space-y-4">
          <input type="password" value={form.currentPassword} onChange={e => setForm(f => ({ ...f, currentPassword: e.target.value }))} placeholder="Current / Temporary Password" required className="w-full px-4 py-2.5 border rounded-lg text-sm" />
          <input type="password" value={form.newPassword} onChange={e => setForm(f => ({ ...f, newPassword: e.target.value }))} placeholder="New Password (min 8 chars)" required className="w-full px-4 py-2.5 border rounded-lg text-sm" />
          <input type="password" value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} placeholder="Confirm New Password" required className="w-full px-4 py-2.5 border rounded-lg text-sm" />
          <button type="submit" disabled={saving} className="w-full py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg disabled:opacity-50">{saving ? 'Changing...' : 'Set New Password'}</button>
        </form>
      </div>
    </div>
  );
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin h-8 w-8 border-4 border-brand-600 border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (user.force_password_change) return <ForcePasswordChange />;
  return children;
}

function AdminRoute({ children }) {
  const { isAdmin, user } = useAuth();
  const ok = isAdmin || user?.role === 'hr';
  return ok ? children : <Navigate to="/dashboard" />;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login />} />
      <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
        <Route index element={<Navigate to="/dashboard" />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="projects" element={<Projects />} />
        <Route path="projects/:projectId/*" element={<ProjectDetail />} />
        <Route path="attendance" element={<Attendance />} />
        <Route path="leave" element={<Leave />} />
        <Route path="purchase-orders" element={<PurchaseOrders />} />
        <Route path="expenses" element={<ExpenseRequests />} />
        <Route path="work-orders" element={<WorkOrders />} />
        <Route path="factory-work-orders" element={<FactoryWorkOrders />} />
        <Route path="wo-tracker" element={<WOTracker />} />
        <Route path="transport-docs" element={<TransportDocs />} />
        <Route path="vendors" element={<Vendors />} />
        <Route path="accounts" element={<Accounts />} />
        <Route path="products" element={<AvProducts />} /> {/* AVB3-route */}
        <Route path="supervisor-ledger" element={<SupervisorLedger goBack={() => window.history.back()} />} />
        <Route path="factory" element={<Factory />} />
        <Route path="profile" element={<Profile />} />
        <Route path="admin/*" element={<AdminRoute><AdminPanel /></AdminRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
}

class ErrorBoundary extends Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('App crash:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px', background: '#f8fafc' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>Something went wrong</h2>
          <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '20px', textAlign: 'center' }}>The app encountered an error. Please refresh to continue.</p>
          <button onClick={() => { this.setState({ hasError: false }); window.location.href = '/dashboard'; }}
            style={{ padding: '12px 32px', background: '#2563eb', color: '#fff', borderRadius: '12px', border: 'none', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
            Refresh App
          </button>
          <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '12px' }}>{this.state.error?.message}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
